import { Delta, Op } from 'quill/core';
import {
    AISharedDeltaReference,
    AISharedGenericReference,
    AISharedReferenceType,
} from '../models/ai-shared.model';

/** Quill blot name of the in-text reference chip. */
export const AI_REFERENCE_BLOT_NAME = 'aiReference';
export const AI_REFERENCE_CLASS = 'ai-reference';
/** Dataset key the blot writes the reference id to and the click handler reads. */
export const AI_REFERENCE_ID_DATASET_KEY = 'referenceId';

/**
 * Wire format of an in-text reference: the BE emits the marker as visible text
 * (`[post:4]`) linked to the reference id, since Delta has no proprietary embed.
 */
const REFERENCE_LINK_MATCHER = /^interacta:reference:(\d+)$/;

/** Value persisted in the Delta embed and rendered by AIReferenceBlot. */
export type AIReferenceBlotValue = {
    id: number;
    label: string;
};

/** Translator signature, so the chip labels stay out of Angular's DI. */
export type AIReferenceTranslate = (
    key: string,
    params?: Record<string, unknown>,
) => string;

export function getReferenceName(
    reference: AISharedGenericReference,
    translate: AIReferenceTranslate,
): string {
    switch (reference.type) {
        case AISharedReferenceType.POST:
            return reference.post.title || translate('POST.NO_TITLE');
        case AISharedReferenceType.COMMENT:
            return translate('AI.REFERENCE_COMMENT_TITLE', {
                author: reference.comment.creatorUser?.fullName,
            });
        default:
            return reference.attachment.attachmentData.name ?? '';
    }
}

function toReferenceId(op: Op): number | null {
    const link = op.attributes?.['link'];
    if (typeof op.insert !== 'string' || typeof link !== 'string') return null;
    const match = REFERENCE_LINK_MATCHER.exec(link);
    return match ? +match[1] : null;
}

/**
 * Turns the BE reference markers into chip embeds. A marker whose reference has
 * no renderable source is dropped altogether: the answer keeps reading as plain
 * text instead of showing a chip that opens nothing.
 *
 * A chip shows the source name when it points at one source, the source count
 * when it points at several.
 */
export function withAIReferenceChips(
    delta: Delta,
    references: AISharedDeltaReference[],
    translate: AIReferenceTranslate,
): Delta {
    const referenceById = new Map(references.map((r) => [r.id, r]));
    const ops: Op[] = [];

    for (const op of delta.ops) {
        const referenceId = toReferenceId(op);
        if (referenceId == null) {
            ops.push(op);
            continue;
        }

        const sources = referenceById.get(referenceId)?.sources ?? [];
        if (!sources.length) continue;

        ops.push({
            insert: {
                [AI_REFERENCE_BLOT_NAME]: {
                    id: referenceId,
                    label:
                        sources.length === 1
                            ? getReferenceName(sources[0], translate)
                            : translate('AI.REFERENCE_SOURCES_COUNT', {
                                  count: sources.length,
                              }),
                } satisfies AIReferenceBlotValue,
            },
        });
    }

    return new Delta(ops);
}
